import React, {
    createContext,
    useContext,
    useEffect,
    useState,
    useCallback,
    useRef,
} from "react";
import { useAuth } from "./AuthContext";
import type { Bot, BotMethod, MethodProgress, WSOutgoingMessage } from "../types/api";

/** Incoming WebSocket message from the server */
interface WSIncomingMessage {
    type: string;
    instanceId?: string;
    methodType?: string;
    isRunning?: boolean;
    params?: Record<string, unknown>;
    methods?: Record<string, BotMethod>;
    progress?: Record<string, MethodProgress>;
    reservedWallets?: { index: number; address: string }[];
    details?: MethodProgress & { balances?: { sol: number; spl: number; wsol: number; updatedAt: number }; walletIndex?: number };
}

interface WSContextType {
    bots: Bot[];
    setBots: React.Dispatch<React.SetStateAction<Bot[]>>;
    send: (message: WSOutgoingMessage) => boolean;
}

const WebSocketContext = createContext<WSContextType | undefined>(undefined);

export const WebSocketProvider: React.FC<{ children: React.ReactNode }> = ({
    children,
}) => {
    const { token } = useAuth();
    const [bots, setBots] = useState<Bot[]>([]);
    const [ws, setWs] = useState<WebSocket | null>(null);
    const socketRef = useRef<WebSocket | null>(null);
    const reconnectTimeoutRef = useRef<number | null>(null);
    const reconnectAttemptsRef = useRef(0);
    const manualCloseRef = useRef(false);

    useEffect(() => {
        // Reset bots when token changes (new login or logout)
        setBots([]);

        manualCloseRef.current = false;
        if (!token) {
            manualCloseRef.current = true;
            if (reconnectTimeoutRef.current !== null) {
                window.clearTimeout(reconnectTimeoutRef.current);
                reconnectTimeoutRef.current = null;
            }
            if (socketRef.current) {
                socketRef.current.close();
                socketRef.current = null;
            }
            return;
        }

        const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
        const wsUrl = `${protocol}//${window.location.host}/ws`;

        const connect = () => {
            if (manualCloseRef.current) return;
            const socket = new WebSocket(wsUrl);
            socketRef.current = socket;

            socket.onopen = () => {
                console.log("WebSocket connected");
                reconnectAttemptsRef.current = 0;
                socket.send(JSON.stringify({ type: 'auth', token }));
                setWs(socket);
            };

            socket.onmessage = (event) => {
                let data: WSIncomingMessage;
                try {
                    data = JSON.parse(event.data);
                } catch (err) {
                    console.error("Failed to parse WS message:", err);
                    return;
                }
                console.log("WS message:", data);

            if (data.type === "update" || data.type === "status") {
                setBots((prev) => {
                    const existingBot = prev.find(b => b.instanceId === data.instanceId);
                    
                    if (existingBot) {
                        return prev.map(b => {
                            if (b.instanceId !== data.instanceId) return b;
                            
                            if (data.methodType) {
                                return {
                                    ...b,
                                    methods: {
                                        ...b.methods,
                                        [data.methodType]: {
                                            isRunning: data.isRunning ?? false,
                                            params: (data.params || b.methods[data.methodType]?.params || {}) as BotMethod["params"],
                                        },
                                    },
                                    progress: data.progress ? { ...b.progress, ...data.progress } : b.progress,
                                    reservedWallets: data.reservedWallets ?? b.reservedWallets,
                                };
                            }
                            
                            if (data.methods) {
                                return {
                                    ...b,
                                    methods: { ...b.methods, ...data.methods },
                                    progress: data.progress ? { ...b.progress, ...data.progress } : b.progress,
                                    reservedWallets: data.reservedWallets ?? b.reservedWallets,
                                };
                            }
                            
                            return b;
                        });
                    } else if (data.instanceId) {
                        const newBot: Bot = {
                            instanceId: data.instanceId,
                            methods: {},
                            reservedWallets: data.reservedWallets,
                            progress: data.progress,
                        };

                        if (data.methodType) {
                            newBot.methods[data.methodType] = {
                                isRunning: data.isRunning ?? false,
                                params: (data.params || {}) as BotMethod["params"],
                            };
                        } else if (data.methods) {
                            newBot.methods = data.methods;
                        }
                        
                        return [...prev, newBot];
                    }
                    return prev;
                });
                } else if (data.type === "progress" && data.methodType) {
                    const methodType = data.methodType;
                    setBots((prev) =>
                        prev.map((b) => {
                            if (b.instanceId !== data.instanceId) return b;
                            const shouldUpdateFunding =
                                data.details?.balances &&
                                (data.details?.walletIndex === undefined ||
                                    data.details?.walletIndex === null);
                            return {
                                ...b,
                                progress: {
                                    ...b.progress,
                                    [methodType]: {
                                        ...b.progress?.[methodType],
                                        ...data.details,
                                    },
                                },
                                fundingBalance: shouldUpdateFunding
                                    ? data.details?.balances
                                    : b.fundingBalance,
                            };
                        }),
                    );
                } else if (data.type === "delete") {
                    setBots((prev) =>
                        prev.filter((b) => b.instanceId !== data.instanceId),
                    );
                }
            };

            socket.onclose = () => {
                console.log("WebSocket closed");
                setWs(null);
                if (manualCloseRef.current) return;
                const attempt = reconnectAttemptsRef.current + 1;
                reconnectAttemptsRef.current = attempt;
                const delay = Math.min(30000, 1000 * 2 ** (attempt - 1));
                reconnectTimeoutRef.current = window.setTimeout(connect, delay);
            };

            socket.onerror = (err) => {
                console.error("WebSocket error:", err);
            };
        };

        connect();

        return () => {
            manualCloseRef.current = true;
            if (reconnectTimeoutRef.current !== null) {
                window.clearTimeout(reconnectTimeoutRef.current);
                reconnectTimeoutRef.current = null;
            }
            if (socketRef.current) {
                // Detach handlers so the deferred onclose from this socket
                // can't stomp the next generation's ws state or schedule a
                // phantom reconnect (matters under React.StrictMode double-mount).
                socketRef.current.onopen = null;
                socketRef.current.onmessage = null;
                socketRef.current.onclose = null;
                socketRef.current.onerror = null;
                socketRef.current.close();
                socketRef.current = null;
            }
        };
    }, [token]); // ← Key: Re-run when token changes

    const send = useCallback(
        (message: WSOutgoingMessage): boolean => {
            const live = socketRef.current;
            if (live && live.readyState === WebSocket.OPEN) {
                console.log("Sending WS message:", message);
                live.send(JSON.stringify(message));
                return true;
            }
            console.warn("WebSocket not ready, message not sent:", message,
                "wsReadyState:", ws?.readyState,
                "refReadyState:", live?.readyState);
            return false;
        },
        [ws],
    );

    return (
        <WebSocketContext.Provider value={{ bots, setBots, send }}>
            {children}
        </WebSocketContext.Provider>
    );
};

export const useWS = () => {
    const context = useContext(WebSocketContext);
    if (!context)
        throw new Error("useWS must be used within WebSocketProvider");
    return context;
};
